/**
 * Conditional routing - rules that re-route a claim when it reaches a
 * state and the conditions match. Each row reads as a sentence: WHEN
 * [state badges] and [humanised conditions] -> assign to X (strategy),
 * then run action Y. Drag to reorder - conditional rules are also
 * first-match-wins by priority.
 */

import { useCallback, useMemo, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GitBranch, GripVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { WorkflowStateBadge } from '@/shared/components/workflows'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'
import {
  useDeleteRoutingRule,
  useReorderRoutingRules,
  type RoutingMeta,
  type RoutingRule,
} from '@/admin/hooks/use-routing-rules'
import { summariseConditions, useStateCatalog } from './routing-helpers'
import {
  AssigneeChip,
  ManagedBadge,
  StrategyChip,
  ThenActionChip,
  WorkflowScopeBadge,
} from './shared'

interface ConditionalRoutingSectionProps {
  rules: RoutingRule[]
  meta: RoutingMeta
  onAdd: () => void
  onEdit: (rule: RoutingRule) => void
}

export function ConditionalRoutingSection({
  rules,
  meta,
  onAdd,
  onEdit,
}: ConditionalRoutingSectionProps) {
  const reorder = useReorderRoutingRules()
  const remove = useDeleteRoutingRule()
  const { byRef } = useAvailableRefs({ entityKey: 'claim' })
  const stateCatalog = useStateCatalog(meta)
  const [ruleToDelete, setRuleToDelete] = useState<RoutingRule | null>(null)

  const sorted = useMemo(
    () =>
      [...rules].sort(
        (a, b) => b.priority - a.priority || a.key.localeCompare(b.key),
      ),
    [rules],
  )
  // Managed rules (e.g. the LOA approver rule authored by Settings >
  // Approvals) are read-only here: shown for context, but not draggable,
  // editable, or deletable from this page.
  const managed = useMemo(() => sorted.filter((r) => r.managed_by), [sorted])
  const editable = useMemo(() => sorted.filter((r) => !r.managed_by), [sorted])

  const actionLabel = useMemo(() => {
    const byKey = new Map(meta.action_options.map((a) => [a.key, a.label]))
    return (key: string) => byKey.get(key) ?? key
  }, [meta.action_options])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = editable.findIndex((r) => r.uuid === active.id)
      const newIndex = editable.findIndex((r) => r.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const reordered = [...editable]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)
      reorder.mutate(
        { kind: 'conditional', ordered_uuids: reordered.map((r) => r.uuid) },
        { onError: (err) => toastApiError(err, 'Failed to reorder rules') },
      )
    },
    [editable, reorder],
  )

  return (
    <section className="space-y-3" data-testid="conditional-routing-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[15px] font-medium text-[var(--ink)]">
            Conditional routing
          </h3>
          <p className="text-[12px] text-[var(--ink-3)]">
            Re-route a claim when it reaches a state and your conditions match
            - amounts, claim fields, host address, and more. The first
            matching rule wins.
          </p>
        </div>
        <Button onClick={onAdd} data-testid="add-conditional-rule">
          <Plus className="h-3.5 w-3.5" />
          Add rule
        </Button>
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-6 py-10 text-center">
          <GitBranch className="h-5 w-5 text-[var(--ink-3)]" />
          <p className="text-[13px] text-[var(--ink-2)]">
            No conditional routing rules yet.
          </p>
          <p className="text-[12px] text-[var(--ink-3)]">
            Add a rule to escalate or re-route claims automatically when
            conditions match.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {managed.length > 0 && (
            <div className="overflow-hidden rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
              {managed.map((rule, index) => (
                <ConditionalRuleRow
                  key={rule.uuid}
                  rule={rule}
                  isLast={index === managed.length - 1}
                  meta={meta}
                  byRef={byRef}
                  stateCatalog={stateCatalog}
                  actionLabel={actionLabel}
                />
              ))}
            </div>
          )}
          {editable.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={editable.map((r) => r.uuid)}
                strategy={verticalListSortingStrategy}
              >
                <div className="overflow-hidden rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
                  {editable.map((rule, index) => (
                    <SortableConditionalRuleRow
                      key={rule.uuid}
                      rule={rule}
                      isLast={index === editable.length - 1}
                      meta={meta}
                      byRef={byRef}
                      stateCatalog={stateCatalog}
                      actionLabel={actionLabel}
                      onEdit={() => onEdit(rule)}
                      onDelete={() => setRuleToDelete(rule)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      <ConfirmDeleteDialog
        open={ruleToDelete !== null}
        onOpenChange={(next) => !next && setRuleToDelete(null)}
        title="Delete routing rule?"
        description={
          ruleToDelete
            ? `"${ruleToDelete.name}" will no longer re-route claims.`
            : undefined
        }
        isPending={remove.isPending}
        onConfirm={() => {
          if (!ruleToDelete) return
          remove.mutate(ruleToDelete.uuid, {
            onSuccess: () => {
              setRuleToDelete(null)
              toast.success('Routing rule deleted')
            },
            onError: (err) => toastApiError(err, 'Failed to delete the rule'),
          })
        }}
      />
    </section>
  )
}

interface RowBodyProps {
  rule: RoutingRule
  meta: RoutingMeta
  byRef: (ref: string) => { label: string } | undefined
  stateCatalog: ReturnType<typeof useStateCatalog>
  actionLabel: (key: string) => string
}

function ConditionalRuleBody({
  rule,
  meta,
  byRef,
  stateCatalog,
  actionLabel,
}: RowBodyProps) {
  const conditionSummary = summariseConditions(rule.conditions, byRef)
  return (
    <div className="min-w-0 flex-1 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-[var(--ink)]">{rule.name}</span>
        {!rule.is_active && <Badge variant="outline">Paused</Badge>}
        <ManagedBadge rule={rule} />
        <WorkflowScopeBadge
          workflowKey={rule.workflow_key}
          workflows={meta.workflows ?? []}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-[var(--ink-2)]">
        <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
          When
        </span>
        {rule.trigger_on_creation && (
          <Badge variant="ghost" className="font-normal text-[var(--ink-2)]">
            Claim created
          </Badge>
        )}
        {rule.trigger_states.map((key) => {
          const state = stateCatalog.get(key)
          return (
            <WorkflowStateBadge
              key={key}
              stateKey={key}
              label={state?.label ?? key}
              color={state?.color}
            />
          )
        })}
        {conditionSummary && <span>and {conditionSummary}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <AssigneeChip assignee={rule.assignee} />
        {rule.assignee.kind === 'role' && (
          <StrategyChip
            strategy={rule.strategy}
            strategyOptions={meta.strategy_options}
          />
        )}
        {rule.target_action_key && (
          <ThenActionChip label={actionLabel(rule.target_action_key)} />
        )}
      </div>
    </div>
  )
}

/** Read-only row for a platform-managed rule (no grip, no edit/delete). */
function ConditionalRuleRow({ rule, isLast, ...body }: RowBodyProps & { isLast: boolean }) {
  return (
    <div
      data-testid={`conditional-rule-row-${rule.key}`}
      className={cn(
        'flex items-start gap-3 bg-[var(--bg)] px-4 py-3 pl-12',
        !isLast && 'border-b-[0.5px] border-b-[var(--rule)]',
      )}
    >
      <ConditionalRuleBody rule={rule} {...body} />
    </div>
  )
}

function SortableConditionalRuleRow({
  rule,
  isLast,
  onEdit,
  onDelete,
  ...body
}: RowBodyProps & {
  isLast: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: rule.uuid })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      data-testid={`conditional-rule-row-${rule.key}`}
      className={cn(
        'group flex items-start gap-3 bg-[var(--bg)] px-4 py-3',
        !isLast && 'border-b-[0.5px] border-b-[var(--rule)]',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder rule ${rule.name}`}
        className="mt-0.5 flex h-5 w-5 shrink-0 cursor-grab items-center justify-center text-[var(--ink-3)] hover:text-[var(--ink)] active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      <ConditionalRuleBody rule={rule} {...body} />

      <div className="flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Edit ${rule.name}`}
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Delete ${rule.name}`}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}
